import { useEffect, useLayoutEffect, useRef } from 'react';

import { useRealtime } from './useRealtime';

/**
 * Run something each time the socket comes back after dropping.
 *
 * Rooms live on the socket, and a reconnect is a new socket: the server re-joins what it
 * decides itself, but anything the client asked for — a date, a set of grades — is gone and
 * has to be asked for again. A screen that does not is the worst kind of broken, because it
 * looks exactly like a quiet afternoon.
 *
 * Deliberately silent on the first connect: the mount fetch already covers that, and firing
 * here would double every screen's opening request.
 */
export function useRealtimeResync(onReconnect: () => void): void {
  const { isConnected } = useRealtime();
  // Tracks whether the socket has EVER connected before, not just whether the previous
  // render was connected — a disconnected render leaves this untouched, so the flag survives
  // the drop and correctly marks the next connect as a reconnect rather than a first connect.
  const hasConnectedOnce = useRef(false);
  // Kept current via a layout effect, not a direct write during render — this repo's other
  // realtime hooks (useRealtimeEvent, useRefetchOnRealtime) follow the same pattern so a
  // changing callback identity never itself churns the connect/disconnect effect below.
  const handler = useRef(onReconnect);
  useLayoutEffect(() => {
    handler.current = onReconnect;
  });

  useEffect(() => {
    if (!isConnected) return;
    if (hasConnectedOnce.current) handler.current();
    hasConnectedOnce.current = true;
  }, [isConnected]);
}
